import { ApiError, apiFetch } from '@/lib/apiFetch';
import { barvaNiti } from '@/lib/barvaNiti';
import { useCallback, useEffect, useMemo, useState } from 'react';

type Nit = {
  id: string;
  kod: string;
  barva: string;
  pouziti: string[];
  ks: number;
  kategorie: { nazev: string; ks: number }[];
};

type Data = { nite: Nit[]; bez_niti: { code: string; ks: number }[] };

/**
 * Kontrola nití — které nitě potřebujeme na předskladněné kusy (zůstatek ve
 * výrobě) a na co: řádek = nit, vpravo kusy po kategoriích produktů.
 */
export function KontrolaNitiPage() {
  const [data, setData] = useState<Data | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [hledat, setHledat] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      setData(await apiFetch<Data>('/api/vyroba/striharna/nite'));
    } catch (e: unknown) {
      setError(e instanceof ApiError ? e.message : 'Nepodařilo se načíst potřebu nití.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const rows = useMemo(() => {
    const q = hledat.trim().toLowerCase();
    if (!q) return data?.nite ?? [];
    return (data?.nite ?? []).filter((n) =>
      [n.kod, n.barva, ...n.kategorie.map((k) => k.nazev)].some((v) => v.toLowerCase().includes(q)),
    );
  }, [data, hledat]);
  const bezKs = (data?.bez_niti ?? []).reduce((s, b) => s + b.ks, 0);

  return (
    <>
      <div className="mb-6 flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-2xl font-bold text-text">Kontrola nití</h1>
          <p className="text-sm text-text-secondary mt-0.5 max-w-2xl">
            Které nitě potřebujeme na předskladněné kusy ve výrobě a na jaké produkty. Nitě se berou
            z Databáze produktů (overlock, kapsy, dvojjehla, náplet).
          </p>
        </div>
        <input
          value={hledat}
          onChange={(e) => setHledat(e.target.value)}
          placeholder="Hledat číslo, barvu, produkt…"
          className="w-64 rounded-lg border border-border bg-surface px-3 py-2 text-sm text-text focus:outline-none focus:border-primary"
        />
      </div>

      {loading && <p className="text-sm text-text-muted">Načítám…</p>}
      {error && !loading && (
        <div className="rounded-lg border border-danger/20 bg-danger-light p-4 mb-6 text-sm text-danger">
          {error}{' '}
          <button type="button" onClick={() => void load()} className="underline cursor-pointer">
            Zkusit znovu
          </button>
        </div>
      )}

      {data && !loading && data.bez_niti.length > 0 && (
        <details className="rounded-lg border border-warning/40 bg-warning-light p-4 mb-6 text-sm text-text">
          <summary className="font-semibold cursor-pointer">
            Nezapočteno — {data.bez_niti.length} produktů ({bezKs} ks) nemá vyplněnou žádnou nit
          </summary>
          <p className="mt-2 text-text-secondary">
            {data.bez_niti.map((b) => `${b.code} (${b.ks} ks)`).join(', ')}
          </p>
        </details>
      )}

      {data && !loading && rows.length === 0 && (
        <p className="text-sm text-text-muted">Žádná nit neodpovídá.</p>
      )}

      {rows.length > 0 && !loading && (
        <div className="bg-surface rounded-xl shadow-card overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-border text-left text-xs uppercase tracking-wider text-text-muted">
                <th className="px-4 py-3 w-28">Číslo</th>
                <th className="px-4 py-3">Barva</th>
                <th className="px-3 py-3 text-right">Kusů</th>
                <th className="px-4 py-3">Na co</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((n) => {
                const hex = barvaNiti(n.barva);
                return (
                  <tr key={n.id} className="border-b border-border-light hover:bg-surface-hover">
                    <td className="px-4 py-3 font-mono font-bold text-text whitespace-nowrap">
                      <span className="inline-flex items-center gap-2">
                        <span
                          title={hex ? 'Barva odhadnutá podle názvu' : 'Barvu neznáme'}
                          className="w-6 h-6 rounded-full border border-border shrink-0 bg-[repeating-linear-gradient(45deg,#eee_0_3px,#fff_3px_6px)]"
                          style={hex ? { background: hex } : undefined}
                        />
                        {n.kod || '—'}
                      </span>
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap">
                      <span className="block text-text font-medium">
                        {n.barva || '(bez názvu)'}
                      </span>
                      <span className="block text-xs text-text-muted">{n.pouziti.join(', ')}</span>
                    </td>
                    <td className="px-3 py-3 text-right tabular-nums font-bold text-text">
                      {n.ks}
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex flex-wrap gap-1.5">
                        {n.kategorie.map((k) => (
                          <span
                            key={k.nazev}
                            className="inline-flex items-center rounded-full bg-primary-50 px-2.5 py-0.5 text-xs text-text whitespace-nowrap"
                          >
                            <span className="font-bold tabular-nums mr-1">{k.ks}×</span>
                            {k.nazev}
                          </span>
                        ))}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </>
  );
}
